import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { generatePath, Link } from 'react-router-dom'
import classnames from 'classnames'
import Collapse from 'antd/lib/collapse'

import apiManager from 'helpers/apiManager'
import useQuery from 'hooks/useQuery'
import { ROUTES } from 'helpers/constants'

import Spinner from 'components/Spinner'
import Button from 'components/Button'
import IdealProfileTemplateModal from './IdealProfileTemplateModal'

const { Panel } = Collapse

const ProfileInfo = ({ user }) => {
  const publicProfilePath = useMemo(
    () => generatePath(ROUTES.ADMIN_USER, { id: user.userId || 0 }),
    [user]
  )

  return (
    <div>
      <p>
        <div>
          <b>Profile Info:</b>
        </div>
        {user.profile}
      </p>
      <p>
        <div>
          <b>Trait:</b>
        </div>
        {user.trait}
      </p>
      <p>
        <Collapse>
          <Panel header="Tags from sessions" key="1">
            <p>{user.tags}</p>
          </Panel>
        </Collapse>
      </p>
      {user.userId && (
        <div className="mt-4 font-semibold underline text-center">
          <Link to={publicProfilePath} target="_blank">
            Visit Profile
          </Link>
        </div>
      )}
    </div>
  )
}

const Card = ({ className, title, children }) => (
  <div className={classnames('w-full mb-4 p-4 rounded-xl shadow-md', className)}>
    {title && <h6 className="text-center underline">{title}</h6>}
    <div>{children}</div>
  </div>
)

const AdminUserAI = ({ match }) => {
  const {
    params: { id },
  } = match
  const [idealProfilePromptTemplate, setIdealProfilePromptTemplate] = useState()
  const [showTemplatePromptModal, setShowTemplatePromptModal] = useState(false)

  const { data, refetch, isLoading } = useQuery(
    ['admin/users/ai', id],
    () =>
      apiManager.adminGetAICompatibilityProfiles({
        userId: id,
        data: {
          idealProfilePromptTemplate,
        },
      }),
    {
      refetchOnWindowFocus: false,
    }
  )

  useEffect(() => {
    setIdealProfilePromptTemplate(data?.idealProfilePromptTemplate)
  }, [data])

  const handleNewIdealProfilePromptTemplate = useCallback(
    async newTemplate => {
      setIdealProfilePromptTemplate(newTemplate)

      return new Promise((resolve, reject) => {
        setTimeout(() => {
          refetch()
            .then(() => resolve())
            .catch(reject)
        }, 1000)
      })
    },
    [refetch]
  )

  return (
    <div className="w-full min-h-screen px-4 bg-white py-4 md:px-10">
      {isLoading ? (
        <div className="h-72 w-full flex items-center justify-center">
          <Spinner />
        </div>
      ) : data ? (
        <div>
          <h3 className="text-center">AI Profile Compatibility</h3>
          <div className="flex space-x-4 mb-10">
            <Card title="User's Profile">
              <ProfileInfo user={data.userProfile} />
            </Card>
            <Card title="Ideal Profile">
              <ProfileInfo user={data.idealProfile} />
              <div className="flex justify-center w-full pt-8">
                <Button
                  className="bg-red-500 text-sm h-10"
                  onClick={() => setShowTemplatePromptModal(true)}
                >
                  Update Prompt
                </Button>
              </div>
            </Card>
          </div>
          <div>
            <h6 className="text-center underline">Ideal Matches</h6>
            {data.idealMatches.map(user => (
              <Card key={user.userId} className="mb-6">
                <div>
                  <span className="mr-4">
                    <b>Average Distance: {user.averageDistance.toFixed(3)};</b>
                  </span>
                  {user.distances.map(({ distance, attribute }) => (
                    <span className="mr-4" key={attribute}>
                      <b>
                        {attribute} distance: {distance.toFixed(3)};
                      </b>
                    </span>
                  ))}
                </div>
                <ProfileInfo user={user} />
              </Card>
            ))}
          </div>
        </div>
      ) : (
        <h5 className="text-red-500 text-center pt-10">
          Unknown error: please try again or another profile
        </h5>
      )}
      {idealProfilePromptTemplate && (
        <IdealProfileTemplateModal
          show={showTemplatePromptModal}
          idealProfilePromptTemplate={idealProfilePromptTemplate}
          onClose={() => setShowTemplatePromptModal(false)}
          onSubmit={handleNewIdealProfilePromptTemplate}
        />
      )}
    </div>
  )
}

export default AdminUserAI
